import * as React from "react";
import { Button, Input, Modal, notification, Select, Tag } from "antd";
import { linksOf, request } from "../../../services/cpln";
import {
  CreateVolumeSnapshotReducerState,
  RunCreateVolumeSnapshotReducerActionType,
  useRunCreateVolumeSnapshotReducer,
} from "./reducer_createVolumeSnapshot";
import { CommandTagItem } from "../../../components/detail/command/commandTagItem";
import { DatePicker, SinglePreset } from "../../../newcomponents/datepicker";
import { VolumeSet } from "../../../schema/types/volumeSet/entity";

// Helper to get start of day in UTC + add duration
function futureStartOfDay(duration: { weeks?: number; months?: number; years?: number }): string {
  const now = new Date();
  // Start of today in UTC
  now.setUTCHours(0, 0, 0, 0);
  // Add duration
  if (duration.weeks) now.setUTCDate(now.getUTCDate() + duration.weeks * 7);
  if (duration.months) now.setUTCMonth(now.getUTCMonth() + duration.months);
  if (duration.years) now.setUTCFullYear(now.getUTCFullYear() + duration.years);
  return now.toISOString();
}

// Snapshot expiration presets - future dates starting at 00:00:00 UTC
const SNAPSHOT_EXPIRATION_PRESETS: SinglePreset[] = [
  { label: "1 Week", value: futureStartOfDay({ weeks: 1 }) },
  { label: "1 Month", value: futureStartOfDay({ months: 1 }) },
  { label: "3 Months", value: futureStartOfDay({ months: 3 }) },
  { label: "6 Months", value: futureStartOfDay({ months: 6 }) },
  { label: "1 Year", value: futureStartOfDay({ years: 1 }) },
];

interface Props {
  volumeSet: VolumeSet;
  initialLocation?: string;
  initialVolumeIndex?: string;
  onClose: any;
  onDone: any;
  visible: boolean;
}

export const CommandModal_CreateVolumeSnapshot: React.FC<Props> = ({
  volumeSet,
  initialLocation,
  initialVolumeIndex,
  onClose,
  onDone,
  visible,
}) => {
  const selfLink = linksOf(volumeSet).self!;
  const [isLoading, setIsLoading] = React.useState(false);

  const [state, dispatch] = useRunCreateVolumeSnapshotReducer(initialLocation, initialVolumeIndex);

  async function handleConfirm() {
    try {
      setIsLoading(true);

      const spec: CreateVolumeSnapshotReducerState = JSON.parse(JSON.stringify(state));
      if (!state.snapshotExpirationDate) {
        // This is to not send null data
        // @ts-ignore
        delete spec.snapshotExpirationDate;
      } else {
        // Ensure date is in UTC ISO format
        spec.snapshotExpirationDate = new Date(state.snapshotExpirationDate).toISOString();
      }
      // @ts-ignore
      spec.snapshotTags = state.snapshotTags.map((t) => ({ [t.name]: t.value }));

      await request({
        method: "post",
        url: `${selfLink}/-command`,
        body: {
          type: "createVolumeSnapshot",
          spec: spec,
        },
      });
      onDone();
      notification.success({
        message: "Success",
        description: "Created Command.",
      });
    } catch (e) {
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({
        message: "Failed",
        description: errorMessage,
      });
      setIsLoading(false);
    }
  }

  let isValid = true;
  if (!state.location) isValid = false;
  if (!state.volumeIndex) isValid = false;
  if (!state.snapshotName) isValid = false;

  return (
    <Modal
      open={visible}
      title="Create Volume Snapshot"
      okText={`Create`}
      onCancel={onClose}
      onOk={handleConfirm}
      cancelButtonProps={{ disabled: isLoading }}
      okButtonProps={{ loading: isLoading, disabled: isLoading || !isValid, className: "action" }}
      maskClosable={false}
      destroyOnHidden
    >
      <div>
        <div>Location</div>
        <Select
          className="mb-4"
          value={state.location}
          onChange={(value) => dispatch({ type: RunCreateVolumeSnapshotReducerActionType.LOCATION, payload: value })}
          options={(volumeSet.status?.locations || []).map(({ name: l }) => ({
            label: l,
            value: l,
            disabled: false,
            isLabel: false,
          }))}
        />
        <div>Volume Index</div>
        <Input
          className="mb-4"
          name={"volumeIndex"}
          value={state.volumeIndex}
          onChange={(e) => dispatch({ type: RunCreateVolumeSnapshotReducerActionType.INDEX, payload: e.target.value })}
        />
        <div>Snapshot Name</div>
        <Input
          className="mb-4"
          name={"snapshotName"}
          value={state.snapshotName}
          onChange={(e) => dispatch({ type: RunCreateVolumeSnapshotReducerActionType.NAME, payload: e.target.value })}
        />
        <div>Snapshot Expiration Date (UTC)</div>
        <DatePicker
          style={{ width: "100%" }}
          className="mb-4"
          value={state.snapshotExpirationDate}
          onChange={(value) =>
            dispatch({
              type: RunCreateVolumeSnapshotReducerActionType.DATE,
              payload: value,
            })
          }
          granularity="second"
          timezoneValue="UTC"
          immediatelyResolveExpressions
          presets={SNAPSHOT_EXPIRATION_PRESETS}
        />
        <div className="flex items-center gap-4 mb-2">
          <div className="flex items-center">
            <div>Snapshot Tags</div>
          </div>
          <Button
            size={"small"}
            type={`primary`}
            onClick={() => dispatch({ type: RunCreateVolumeSnapshotReducerActionType.ADD_TAG, payload: "" })}
          >
            Add
          </Button>
        </div>
        {state.snapshotTags.length > 0 ? null : <Tag>{"No Snapshot Tags"}</Tag>}
        <div className="flex flex-col gap-2">
          {state.snapshotTags.map((tag) => (
            <CommandTagItem
              key={tag.index}
              index={tag.index}
              name={tag.name}
              value={tag.value}
              onChange={(index, name, value) =>
                dispatch({
                  type: RunCreateVolumeSnapshotReducerActionType.CHANGE_TAG,
                  payload: { index, name, value },
                })
              }
              onRemove={() =>
                dispatch({ type: RunCreateVolumeSnapshotReducerActionType.REMOVE_TAG, payload: tag.index })
              }
            />
          ))}
        </div>
      </div>
    </Modal>
  );
};
